import React, { useEffect, useState, useMemo, useRef } from 'react'
import { AgGridReact } from 'ag-grid-react'
import Connect from 'api/Connect'
import {
    AccentArea,
    MerchantSwitcher,
    Metric,
    PresetDateRange,
    useActiveMerchant,
} from 'components'
import idDirectory from './idAttributes'
import { IDateRange } from 'components/PresetDateRange'
import { withRequiredRole } from 'components/useRequireRole'
import { format } from 'date-fns'
import useFeatureToggle from 'hooks/FeatureToggles/useFeatureToggles'
import { useAuthedUser } from 'context/AuthedUser/AuthedUserContext'
import CB from 'lib'
import EntFeature from 'models/EntFeature'
import { dateUtilities } from 'utils/dateUtilities'
import View from 'views/View'
import { ChargebacksByCardType } from './charts/ChargebacksByCardType'
import { ChargebacksByPostDate } from './charts/ChargebacksByPostDate'
import { ChargebacksByStatusPie } from './charts/ChargebacksByStatus'
import { MidTop5DataAGGrid } from './grids/MidTop5DataAGGrid'
import { getMetrics } from './metrics_config/metrics-config'
import { iconsTheme } from 'theme-exports'
import Icon from 'components/Icon'
import { useAGDataGridActions } from 'hooks/useAGDataGridActions/useAGDataGridActions'
import { useThemeStyling } from 'hooks/useThemeStyling/useThemeStyling'
import { ExtendedColumn } from 'components/DataGrid/ExtendedColumn'
import { randomizeValue } from 'utils/randomizers'
import { getDashboardTilesMockData } from 'utils/clientMockData'
import { CLIENT_THEME_VARIANT } from 'hooks/useThemeStyling/useThemeStyling'

interface Top5MidData {
    mid: number | string
    merchantIdentifier: number | string
    midAlias: string
    newCases: number | string
    nonFraud: number | string
    percentNonFraud: number | string
    fraud: number | string
    percentFraud: number | string
    defended: number | string
}

interface Top5MidState {
    data: Top5MidData[]
    date_range?: string
    loading: boolean
    error: boolean | Record<string, string | number>
}

export interface CardType {
    case_status: string
    count: number
}

interface ChargebacksByCardTypeState {
    loading: boolean
    date_range: string
    payload: {
        cardTypes: any
    }
}

interface ChargebacksByStatusType {
    loading: boolean
    date_range: string
    payload: {
        caseStatusData: CardType[]
    }
}

interface ChargebackByPostDate {
    date: string
    count: number
}

export interface ChargebacksByPostDateProps {
    chartData: ChargebackByPostDate[]
    loading: boolean
}

interface ChargebacksByPostDateState {
    loading: boolean
    date_range: string
    payload: {
        chargebacks: ChargebackByPostDate[]
    }
}

interface IMetric {
    metric: string | number
    decorator: null | string
    title: string
    timeframe: string
    link: {
        to: string
        text: string
    }
    intent: 'warning' | 'success' | 'error' | 'info'
    icon: any
    backgroundColor?: string
    borderTopColor?: string
    variant: 'default' | 'alt'
}

interface FormattedMetricDataState {
    loading: boolean
    error: boolean | Record<string, string | number>
    metricData: IMetric[]
    [key: string]: any
}

/** DEFAULT DATE RANGES FOR INPUTS AND API CALLS ONLOAD  */
// date_range default is for the last 7 days
const defaultDateRangeOnLoad: {
    to: Date
    from: Date
} = dateUtilities.getPriorDays(30) // change this to change the default date range (ref: dateUtilities.ts)
const { to, from } = defaultDateRangeOnLoad
const PresetDateRangeDate: IDateRange = {
    to: format(to, 'yyyy-MM-dd'),
    from: format(from, 'yyyy-MM-dd'),
}

const getNext72Hr = () => {
    const d = dateUtilities.getNextDays(3)
    return `${format(d.from, 'yyyy-MM-dd')}|${format(d.to, 'yyyy-MM-dd')}`
}
// default api call date range on load
const initialApiDateRange = `${PresetDateRangeDate.from}|${PresetDateRangeDate.to}`

/** The Dashboard view with it's graphs */
export const Dashboard = () => {
    const testId = 'dashboard'
    const { id } = useActiveMerchant()
    const { title_icon: titleIconEnabled } = useFeatureToggle('ACCENT_AREA')
    const { themeVariantObj } = useThemeStyling()
    const [dataGridColumns, setDataGridColumns] = useState<ExtendedColumn[]>([])
    // const { setShowNewDataGrid, showNewDataGrid } = useAuthedUser()
    const { user } = useAuthedUser()
    const top5MidGridRef = useRef<AgGridReact>(null)

    const showClientTypeIssuerUI = user?.is_issuer ?? false

    const [chargebacksSum, setChargebacksSum] = useState<number>()

    // TODO - replace this with the above merchant context once that is fully working.
    const [top5MidData, setTop5MidData] = useState<Top5MidState>({
        data: [],
        loading: true,
        error: false,
        date_range: initialApiDateRange,
    })
    // Use to run API call even if same date range given for Top 5 Mids
    const [runTop5Mids, setRunTop5Mids] = useState(false)

    // Needed for Top 5 Mid AG Grid
    const filterValuesMemo = useMemo(() => {
        return {
            client_id: id,
            limit: undefined,
            start: undefined,
            sort_order: undefined,
            date_range: top5MidData.date_range,
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, top5MidData.date_range])

    // Needed to prevent Top 5 from staying in loading if Merchant switcher or date changed since using mock data
    const filterValuesMemoMock = useMemo(() => {
        return {
            client_id: id,
            limit: undefined,
            start: undefined,
            sort_order: undefined,
            date_range: top5MidData.date_range,
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const updatedFilterValues =
        showClientTypeIssuerUI || [8].includes(themeVariantObj.id)
            ? filterValuesMemoMock
            : filterValuesMemo

    const top5DataGridEndpoint = showClientTypeIssuerUI
        ? '/cm/issuer/reports/top5-account-numbers'
        : '/cm/cases/reports/mids-top5'

    const { dataSource, isLoading, paginationTotal } = useAGDataGridActions(
        top5DataGridEndpoint,
        top5MidGridRef,
        +id,
        updatedFilterValues,
        {}
    )

    // once all raw metric data is loaded it is formatted into an array and data shape that works with Metric
    const [
        formattedMetricData,
        setFormattedMedtricData,
    ] = useState<FormattedMetricDataState>({
        loading: true, //loading intially awaiting metric data
        error: false,
        metricData: [], // needs initial data to show loading
    })
    // chargeback by card type data 'default is to return one years worth for now'
    const [
        caseStatusData,
        setCaseStatusData,
    ] = useState<ChargebacksByStatusType>({
        loading: true,
        date_range: initialApiDateRange,
        payload: {
            caseStatusData: [],
        },
    })

    const [
        cardTypeData,
        setCardTypeData,
    ] = useState<ChargebacksByCardTypeState>({
        loading: true,
        date_range: initialApiDateRange,
        payload: {
            cardTypes: [],
        },
    })

    const [
        chargebacksByPostDate,
        setChargebacksByPostDate,
    ] = useState<ChargebacksByPostDateState>({
        loading: true,
        date_range: initialApiDateRange,
        payload: {
            chargebacks: [],
        },
    })

    // Use to run API call even if same date range given for CB by Post Date
    const [runCbByPostDate, setRunCbByPostDate] = useState(false)

    // Ahhh.. so many endpoints. All for the Metrics.
    const getCasesMtd = React.useCallback(async (): Promise<any> => {
        if (!id) return
        try {
            const casesMtd = await Connect.query(
                { name: 'casesMtd' },
                {
                    method: 'GET',
                    params: {
                        client_id: id,
                    },
                }
            )

            return {
                accessor: 'chargebacks',
                payload: casesMtd,
            }
        } catch (e) {
            return e
        }
    }, [id])

    const getFraudulentCasePct = React.useCallback(async (): Promise<any> => {
        if (!id) return
        try {
            const monthDateObj = dateUtilities.getMonth()
            const fraudPct = (await Connect.query(
                { name: 'casesFraudPct' },
                {
                    method: 'GET',
                    params: {
                        client_id: id,
                        date_range: `${monthDateObj.from}|${monthDateObj.to}`,
                    },
                }
            )) as { ratio: string }

            return {
                accessor: 'caseFraudPercent',
                // TODO - this is a hack to get the data into the right shape.
                payload: {
                    ratio: `${fraudPct.ratio.split('.')[0].replace('%', '')}%`,
                },
            }
        } catch (e) {
            return e
        }
    }, [id])

    const getNonFraudulentCasePct = React.useCallback(async (): Promise<any> => {
        if (!id) return
        try {
            const monthDateObj = dateUtilities.getMonth()
            const nonFraudPct = (await Connect.query(
                { name: 'issuerCasesNonFraudPct' },
                {
                    method: 'GET',
                    params: {
                        client_id: id,
                        date_range: `${monthDateObj.from}|${monthDateObj.to}`,
                    },
                }
            )) as { ratio: string }

            return {
                accessor: 'caseNonFraudPercent',
                payload: {
                    ratio: `${nonFraudPct.ratio
                        .split('.')[0]
                        .replace('%', '')}%`,
                },
            }
        } catch (e) {
            return {
                accessor: 'caseNonFraudPercent',
                payload: {
                    ratio: `0%`,
                },
            }
        }
    }, [id])

    const getChargebacksExpiring = React.useCallback(async () => {
        if (!id) return
        try {
            const res = await CB.cases.list({
                client_id: id,
                status_group_id: '1,2',
                limit: '0',
                due_date_range: getNext72Hr(),
            })

            return {
                accessor: 'chargebacksExpiring',
                payload: res.pagination.total,
            }
        } catch (error) {
            return error
        }
    }, [id])

    const getNewChargebacks = React.useCallback(async () => {
        if (!id) return
        try {
            const res = await CB.cases.list({
                client_id: id,
                status_group_id: '1',
                limit: '0',
            })

            return {
                accessor: 'newChargebacks',
                payload: res.pagination.total,
            }
        } catch (error) {
            return error
        }
    }, [id])

    const getMidCount = React.useCallback(async (): Promise<any> => {
        if (!id) return
        try {
            const midCount = await Connect.query(
                { name: 'midCount' },
                {
                    method: 'GET',
                    params: {
                        client_id: id,
                        date_range: initialApiDateRange,
                    },
                }
            )

            return {
                accessor: 'mids',
                payload: midCount,
            }
        } catch (e) {
            return e
        }
    }, [id])

    const getUsersCount = React.useCallback(async () => {
        if (!id) return
        try {
            const res: any = await Connect.query(
                { name: 'users' },
                {
                    method: 'GET',
                    params: {
                        merchant_id: id,
                        limit: '0',
                    },
                }
            )

            return {
                accessor: 'usersCount',
                payload: res.pagination?.total,
            }
        } catch (error) {
            return error
        }
    }, [id])

    // updates chargebacks by post date range when selecting apply in the preset date range select
    const updateCbByPostDateRange = (date_range: string) => {
        setChargebacksByPostDate({
            ...chargebacksByPostDate,
            date_range,
        })
        setRunCbByPostDate(!runCbByPostDate)
    }

    // updates top 5 mid date range
    const updateTop5MidsDateRange = (date_range: string) => {
        setTop5MidData((prevState) => ({
            ...prevState,
            date_range,
        }))
        setRunTop5Mids(!runTop5Mids)
    }


    const returnMetricEndpoints = React.useCallback(async (): Promise<any> => {
        try {
            const allEndpoints = await Promise.all([
                getCasesMtd(),
                getFraudulentCasePct(),
                getNonFraudulentCasePct(),
                getChargebacksExpiring(),
                getNewChargebacks(),
                getMidCount(),
                getUsersCount(),
            ])

            return allEndpoints
        } catch (e) {
            return e
        }
    }, [
        getCasesMtd,
        getFraudulentCasePct,
        getNonFraudulentCasePct,
        getChargebacksExpiring,
        getNewChargebacks,
        getMidCount,
        getUsersCount,
    ])

    // // call each metric endpoint and return them all at once
    useEffect(() => {
        setFormattedMedtricData((prevState) => ({
            ...prevState,
            loading: true,
        }))
        if (id && chargebacksSum !== undefined) {
            returnMetricEndpoints()
                .then((res: any[]) => {
                    let metricObject: any = {}
                    // loop over each data set in rawMetricData to create an object with
                    res.forEach((dataset: any) => {
                        if (dataset.accessor === 'chargebacks') {
                            metricObject = {
                                ...metricObject,
                                chargebacks: {
                                    // Set mock data to match area chart
                                    metric: chargebacksSum ?? dataset.payload.count,
                                },
                            }
                        }
                        if (dataset.accessor === 'caseFraudPercent') {
                            metricObject = {
                                ...metricObject,
                                caseFraudPercent: {
                                    // Set mock data if no value
                                    metric: parseFloat(
                                        dataset.payload.ratio.replace('%', '')
                                    )
                                        ? dataset.payload.ratio
                                        : '35%',
                                },
                            }
                        }
                        if (dataset.accessor === 'caseNonFraudPercent') {
                            metricObject = {
                                ...metricObject,
                                caseNonFraudPercent: {
                                    metric: dataset.payload.ratio,
                                },
                            }
                        }
                        if (dataset.accessor === 'chargebacksExpiring') {
                            metricObject = {
                                ...metricObject,
                                chargebacksExpiring: {
                                    // Set mock data if no value
                                    metric: dataset.payload || 11,
                                },
                            }
                        }
                        if (dataset.accessor === 'newChargebacks') {
                            metricObject = {
                                ...metricObject,
                                newChargebacks: {
                                    metric: dataset.payload,
                                },
                            }
                        }
                        if (dataset.accessor === 'mids') {
                            metricObject = {
                                ...metricObject,
                                mids: {
                                    metric: dataset.payload.count,
                                },
                            }
                        }
                        if (dataset.accessor === 'usersCount') {
                            metricObject = {
                                ...metricObject,
                                usersCount: {
                                    metric: dataset.payload,
                                },
                            }
                        }
                    })

                    setFormattedMedtricData({
                        error: false,
                        loading: false,
                        metricData: showClientTypeIssuerUI
                            ? getDashboardTilesMockData(
                                  metricObject,
                                  themeVariantObj
                              )
                            : metricObject,
                    })
                })
                .catch((err) => {
                    setFormattedMedtricData({
                        metricData: [],
                        loading: false,
                        error: err,
                    })
                })
        }
    }, [
        id,
        chargebacksSum,
        returnMetricEndpoints,
        themeVariantObj,
        showClientTypeIssuerUI,
    ])

    // // call top 5 mid chart data
    useEffect(() => {
        setTop5MidData((prevState) => ({
            ...prevState,
            loading: true,
        }))
        if (id) {
            Connect.query(
                { name: 'midTop5' },
                {
                    method: 'GET',
                    params: {
                        client_id: id,
                        date_range:
                            top5MidData.date_range ?? initialApiDateRange,
                    },
                }
            )
                .then((res: any) => {
                    const updatedData = res.map((mid: Top5MidData) => ({
                        ...mid,
                        // Duplicating 'midAlias' since Bluesnap requires 2 columns with same mapping - to prevent grid from timing out.
                        midAlias2: mid.midAlias,
                        // Random number between 5 and 30 with 2 decimal places. Mocked for demo purposes.
                        inReviewPercentage: `${(5 + Math.random() * 25).toFixed(
                            2
                        )}%`,
                    }))
                    setTop5MidData((prev) => ({
                        ...prev,
                        loading: false,
                        error: false,
                        data: updatedData,
                    }))
                })
                .catch((error) => {
                    setTop5MidData((prev) => ({
                        ...prev,
                        loading: false,
                        error,
                        data: [],
                    }))
                })
        }
    }, [top5MidData.date_range, id, runTop5Mids])

    // // call chargebacks by post date
    useEffect(() => {
        const fetchData = async (
            dateRange: string = chargebacksByPostDate.date_range
        ) => {
            // Mock Data
            const mockData = await import(
                './mocks/mockChargebacksByPostDateData.json'
            )

            // use dynamic dates
            const [fromDate, toDate] = dateRange.split('|')
            const numberOfDataPoints = mockData.data.length
            const newDates = dateUtilities.getDateDomain({
                from: fromDate,
                to: toDate,
                numberOfPoints: numberOfDataPoints,
            })

            const updatedMockData: ChargebackByPostDate[] = mockData.data.map(
                (data: ChargebackByPostDate, idx: number) => {
                    return {
                        ...data,
                        date: newDates[idx],
                    }
                }
            )

            // Now get all data points for this month
            const currentMonth = new Date().getUTCMonth()
            const currentYear = new Date().getUTCFullYear()
            const currentMonthData = updatedMockData.filter((data) => {
                const date = new Date(data.date)
                return (
                    date.getUTCMonth() === currentMonth &&
                    date.getUTCFullYear() === currentYear
                )
            })
            // Now with this months data, compute totals for Chargebacks/Cases
            setChargebacksSum(
                currentMonthData.reduce((acc, data) => acc + data.count, 0)
            )

            return { data: updatedMockData }

            // // API
            // return await Connect.query(
            //     { name: 'chargebacksByPostDate' },
            //     {
            //         method: 'GET',
            //         params: {
            //             client_id: id,
            //             date_range: chargebacksByPostDate.date_range,
            //         },
            //     }
            // )
        }

        setChargebacksByPostDate((prevState) => ({
            ...prevState,
            loading: true,
        }))
        if (id) {
            fetchData()
                .then(({ data }) =>
                    setChargebacksByPostDate((prevState) => ({
                        ...prevState,
                        loading: false,
                        payload: {
                            chargebacks: data,
                        },
                    }))
                )
                .catch((e) => e)
        }
    }, [chargebacksByPostDate.date_range, id, runCbByPostDate]) // recalls api anytime date_range or client_id is updated if we want to integrate some sort of date range / merchant switcher selection later

    // // call chargebacks by card type
    useEffect(() => {
        setCardTypeData((prevState) => ({
            ...prevState,
            loading: true,
        }))
        if (!id) return

        const BASE_CARD_BRANDS: BaseCardTypeItem[] = [
            { name: 'Visa', description: 'Visa', base: 14 },
            { name: 'Mastercard', description: 'Mastercard', base: 11 },
            { name: 'Amex', description: 'Amex', base: 9 },
            { name: 'Discover', description: 'Discover', base: 7 },
        ]

        const BASE_ISSUER: BaseCardTypeItem[] = [
            { name: 'Mastercard', description: 'Mastercard', base: 15 },
            { name: 'Visa', description: 'Visa', base: 1 },
        ]

        const BASE_REASON_CODES: BaseCardTypeItem[] = [
            {
                name: 'Visa 10.4',
                description: 'Visa 10.4 - Other Fraud',
                base: 14,
            },
            {
                name: 'MC 4837',
                description: 'MC 4837 - No Cardholder Authorization',
                base: 11,
            },
            {
                name: 'Visa 13.1',
                description: 'Visa 13.1 - Merchandise Not Received',
                base: 9,
            },
            {
                name: 'MC 4853',
                description: 'MC 4853 - Cardholder Dispute',
                base: 7,
            },
        ]

        // Using randomized values close to configured bases instead of API call
        setTimeout(() => {
            const isBrandVariant = [
                    CLIENT_THEME_VARIANT.DISPUTE_PRO.id,
                    CLIENT_THEME_VARIANT.LVMH.id,
                    CLIENT_THEME_VARIANT.CB911.id,
                    CLIENT_THEME_VARIANT.PAYMENTSAVE.id,
                ].includes(themeVariantObj.id)
            const baseItems = isBrandVariant
                ? BASE_CARD_BRANDS
                : showClientTypeIssuerUI
                ? BASE_ISSUER
                : BASE_REASON_CODES

            // Keep percent variance small so data looks realistic and close to original configs
            const randomized = buildCardTypes(baseItems, 25)

            setCardTypeData((prevState) => ({
                ...prevState,
                loading: false,
                payload: { cardTypes: randomized },
            }))
        }, Math.random() * 1000) // Small delay to simulate API call

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cardTypeData.date_range, id])

    useEffect(() => {
        setCaseStatusData((prevState) => ({
            ...prevState,
            loading: true,
        }))
        if (id) {
            CB.reports
                .chargebacksByStatus(id, {
                    date_range: caseStatusData.date_range,
                })
                .then((r) =>
                    setCaseStatusData((prevState) => ({
                        ...prevState,
                        loading: false,
                        payload: {
                            // @ts-ignore
                            caseStatusData: r,
                        },
                    }))
                )
                .catch((e) => e)
        }
    }, [caseStatusData.date_range, id])

    // Pass in some template view data up front

    const { METRICS, CHARTS } = useFeatureToggle('DASHBOARD')

    const metrics = getMetrics(
        showClientTypeIssuerUI ? METRICS.metric_variant : 'cb911'
    )

    // const handleSwitchGrids = () => {
    //     setShowNewDataGrid && setShowNewDataGrid(!showNewDataGrid)
    // }

    return (
        <>
            <View
                title="Dashboard"
                breadcrumb="Dashboard"
                HeaderInterior={<MerchantSwitcher testId={testId} />}
                testId={testId}
            >
                <div id={idDirectory.divGrid} className={'emp-grid'}>
                    {METRICS.enabled
                        ? Object.entries(metrics).map(([key, metric]: any) => (
                              <div
                                  id={`${idDirectory.divMetric}-${key}`}
                                  key={`${key}-metric`}
                                  className={'emp-metric'}
                              >
                                  <Metric
                                      loading={formattedMetricData.loading}
                                      metric={metric}
                                      key={metric.title}
                                      testId={`${testId}-${key}`}
                                      metricValue={formattedMetricData.metricData?.[
                                          key
                                      ]?.metric.toLocaleString('en-US')}
                                      showClientTypeIssuerUI={
                                          showClientTypeIssuerUI
                                      }
                                  />
                              </div>
                          ))
                        : null}
                    <AccentArea
                        className={'emp-lineChart'}
                        title={
                            <div className={'emp-icon'}>
                                {titleIconEnabled && (
                                    <Icon
                                        className={`${
                                            iconsTheme.calendar
                                        } ${'emp-marginRight'}`}
                                    />
                                )}
                                <span>
                                    {showClientTypeIssuerUI
                                        ? 'Cases by Creation Date'
                                        : 'Chargebacks by Post Date'}
                                </span>
                            </div>
                        }
                        headerAction={
                            <PresetDateRange
                                getDateRange={updateCbByPostDateRange}
                                dateRange={PresetDateRangeDate}
                                className={'emp-dateRangePicker'}
                                allowFutureDateSelection={false}
                                testId={`${testId}-cbByPostDate`}
                            />
                        }
                        testId={`${testId}-cbByPostDate`}
                    >
                        <ChargebacksByPostDate
                            data={chargebacksByPostDate.payload.chargebacks}
                            loading={chargebacksByPostDate.loading}
                            dateRange={chargebacksByPostDate.date_range}
                            showClientTypeIssuerUI={showClientTypeIssuerUI}
                        />
                    </AccentArea>

                    <AccentArea
                        className={'emp-pieChart'}
                        title={
                            <div className={'emp-icon'}>
                                {titleIconEnabled && (
                                    <Icon
                                        className={`${
                                            iconsTheme.creditCard
                                        } ${'emp-marginRight'}`}
                                    />
                                )}
                                <span>
                                    {showClientTypeIssuerUI
                                        ? CHARTS.pie_chart.title
                                        : 'Chargebacks by Card Type'}
                                </span>
                            </div>
                        }
                        testId={
                            CHARTS.pie_chart.variant === 'chargebacksByStatus'
                                ? `${testId}-cbByStatus`
                                : `${testId}-cbByCardType`
                        }
                    >
                        {CHARTS.pie_chart.variant === 'chargebacksByStatus' && (
                            <ChargebacksByStatusPie
                                loading={caseStatusData.loading}
                                data={caseStatusData.payload.caseStatusData}
                            />
                        )}
                        {CHARTS.pie_chart.variant ===
                            'chargebacksByCardType' && (
                            <ChargebacksByCardType
                                data={cardTypeData.payload.cardTypes}
                                loading={cardTypeData.loading}
                            />
                        )}
                    </AccentArea>
                    <AccentArea
                        className={'emp-table'}
                        title={
                            <div className={'emp-icon'}>
                                {titleIconEnabled && (
                                    <Icon
                                        className={`${
                                            iconsTheme.briefcase
                                        } ${'emp-marginRight'}`}
                                    />
                                )}
                                <span>
                                    {[8].includes(themeVariantObj.id) // LMVH
                                        ? 'Top 5 Brands by Number of Cases'
                                        : showClientTypeIssuerUI
                                        ? 'Top 5 Clients by Number of Cases'
                                        : 'Top 5 MIDs by Number of New Chargebacks'}
                                </span>
                            </div>
                        }
                        headerAction={
                            <div className="emp-top5ActionsContainer">
                                <PresetDateRange
                                    getDateRange={updateTop5MidsDateRange}
                                    dateRange={PresetDateRangeDate}
                                    className={'emp-dateRangePicker'}
                                    allowFutureDateSelection={false}
                                    testId={`${testId}-top5MidsByNumberOfNewCB`}
                                />
                            </div>
                        }
                        testId={`${testId}-top5MidsByNumberOfNewCB`}
                    >
                        {/* {showNewDataGrid ? ( */}
                        <MidTop5DataAGGrid
                            gridRef={top5MidGridRef}
                            testId={`${testId}-top5MidsByNumberOfNewCB`}
                            dataSource={dataSource}
                            isLoading={isLoading}
                            paginationTotal={paginationTotal}
                            showClientTypeIssuerUI={showClientTypeIssuerUI}
                            dataGridColumns={dataGridColumns}
                            setDataGridColumns={setDataGridColumns}
                            chargebacksSum={chargebacksSum}
                        />
                        {/* ) : (
                            <MidTop5DataGrid
                                data={top5MidData?.data}
                                loading={top5MidData?.loading}
                                testId={`${testId}-top5MidsByNumberOfNewCB`}
                                showClientTypeIssuerUI={showClientTypeIssuerUI}
                            />
                        )} */}
                    </AccentArea>
                </div>
            </View>
        </>
    )
}

export default withRequiredRole(EntFeature.DASHBOARD, Dashboard)

// --- Helpers for randomized mock data (card types) ---
type BaseCardTypeItem = { name: string; description: string; base: number }

const computePercents = (counts: number[]): number[] => {
    const total = counts.reduce((a, b) => a + b, 0) || 1
    const pcts: number[] = []
    let sum = 0
    for (let i = 0; i < counts.length - 1; i++) {
        const p = parseFloat(((counts[i] / total) * 100).toFixed(2))
        pcts.push(p)
        sum += p
    }
    const last = parseFloat((100 - sum).toFixed(2))
    pcts.push(last)
    return pcts
}

const buildCardTypes = (
    baseItems: BaseCardTypeItem[],
    variance?: number
): Array<{
    name: string
    description: string
    count: number
    pct: number
}> => {
    const counts = baseItems.map((it) => randomizeValue(it.base, variance))
    const pcts = computePercents(counts)
    return baseItems.map((it, idx) => ({
        name: it.name,
        description: it.description,
        count: counts[idx],
        pct: pcts[idx],
    }))
}
